import { useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { toast } from '@/lib/swal';
import { compressPhoto } from '../photo-compress.util';
import { MAX_PHOTOS, PHOTO_MIME } from '../intake-form.constants';

export interface PhotoEntry {
  file: File;
  previewUrl: string;
}

interface PhotoUploadSectionProps {
  existingUrls: string[];
  removedIndices: number[];
  pendingEntries: PhotoEntry[];
  onRemoveExisting: (idx: number) => void;
  onAddFiles: (files: File[]) => void;
  onRemovePending: (idx: number) => void;
  disabled: boolean;
}

/** Reused verbatim from the original EquipmentIntakePage modal form — extracted
 * only so the Equipment Intake Workspace (Feature 5) can reuse it as-is. */
export function PhotoUploadSection({
  existingUrls,
  removedIndices,
  pendingEntries,
  onRemoveExisting,
  onAddFiles,
  onRemovePending,
  disabled,
}: PhotoUploadSectionProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const visibleExisting = existingUrls.filter((_, i) => !removedIndices.includes(i));
  const total = visibleExisting.length + pendingEntries.length;

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    const available = MAX_PHOTOS - total;
    if (available <= 0) return;
    const toProcess = files.slice(0, available);
    const results: File[] = [];
    for (const f of toProcess) {
      if (!PHOTO_MIME.includes(f.type)) {
        toast.error('Only JPG, PNG, WEBP accepted');
        continue;
      }
      try {
        results.push(await compressPhoto(f));
      } catch {
        toast.error(`Failed to process ${f.name}`);
      }
    }
    if (results.length > 0) onAddFiles(results);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {existingUrls.map((url, i) => {
          if (removedIndices.includes(i)) return null;
          return (
            <div key={`ex-${i}`} className="group relative">
              <img
                src={url}
                className="h-20 w-20 rounded-lg border border-border object-cover"
                alt={`Photo ${i + 1}`}
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={() => onRemoveExisting(i)}
                  className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white shadow"
                  aria-label="Remove photo"
                >
                  <FontAwesomeIcon icon={faTimes} className="h-2.5 w-2.5" />
                </button>
              )}
            </div>
          );
        })}

        {pendingEntries.map((entry, i) => (
          <div key={`pend-${i}`} className="relative">
            <img
              src={entry.previewUrl}
              className="h-20 w-20 rounded-lg border-2 border-dashed border-primary object-cover opacity-80"
              alt="New photo"
            />
            <span className="absolute bottom-0 left-0 right-0 rounded-b-lg bg-primary/80 py-0.5 text-center text-[9px] font-medium text-white">
              NEW
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={() => onRemovePending(i)}
                className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-white shadow"
                aria-label="Remove pending photo"
              >
                <FontAwesomeIcon icon={faTimes} className="h-2.5 w-2.5" />
              </button>
            )}
          </div>
        ))}

        {total < MAX_PHOTOS && !disabled && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
            <span className="text-[10px]">Add</span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        className="sr-only"
        onChange={handleChange}
      />
      <p className="text-xs text-muted-foreground">
        JPG, PNG, WEBP — max {MAX_PHOTOS} photos, compressed to 500 KB
      </p>
    </div>
  );
}
